/**
 * What Congress is working on now, furthest along first.
 *
 * The question this page exists to answer is "is this one going to happen",
 * and the honest answer is not a percentage anyone should trust for a named
 * bill. So it answers a different question accurately instead: here is how
 * far this bill has got, and here is how many bills that got the same far
 * in the last two finished Congresses became law.
 *
 * That is a base rate, and the page says so everywhere it shows one. Both
 * halves of every fraction are on screen -- "1 in 3 (639 of 1,922)" -- so
 * nobody is handed a bare percentage they cannot weigh. A reader who wants
 * to disbelieve it can see exactly what it was drawn from.
 */

import { Link, useSearchParams } from "react-router-dom";
import { api, type MovingBill, type Rung } from "../api";
import { useAsync, useDebounced } from "../useAsync";
import { Problem, Spinner } from "../components/bits";
import { oneIn, reachedLabel, toneFor } from "../components/bill";
import { when } from "../format";

const PER_PAGE = 24;

export function Bills() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const stage = params.get("stage") ?? "";
  const topic = params.get("topic") ?? "";
  const page = Number(params.get("page") ?? 1);

  // Otherwise every keystroke is a request, and they do not come back in
  // the order they were sent.
  const settled = useDebounced(query);

  const bills = useAsync(
    () =>
      api.movingBills({
        page,
        per_page: PER_PAGE,
        stage: stage || undefined,
        policy_area: topic || undefined,
        query: settled || undefined,
      }),
    [settled, stage, topic, page],
  );
  const topics = useAsync(() => api.policyAreas(), []);

  /** Any filter change returns to page one; page 7 of a new filter is empty. */
  function set(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  }

  const filtered = Boolean(query || stage || topic);
  const ladder = bills.data?.ladder ?? [];

  return (
    <>
      <header className="border-b border-line bg-gradient-to-b from-brand/6 to-paper">
        <div className="mx-auto max-w-6xl px-5 py-12">
          <h1 className="font-display text-4xl font-semibold text-balance">
            What Congress is working on
          </h1>
          <p className="mt-3 max-w-2xl text-pretty text-lg text-muted">
            Every bill still alive in the {bills.data ? ordinal(bills.data.congress) : "current"}{" "}
            Congress, closest to becoming law first. Nobody can tell you whether a bill will pass —
            so instead, each one shows how far it has got, and how often bills that got that far
            have made it.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-10">
        {ladder.length > 0 && (
          <Ladder rungs={ladder} active={stage} onPick={(key) => set({ stage: key })} />
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <input
            value={query}
            onChange={(event) => set({ q: event.target.value })}
            type="search"
            aria-label="Search bills"
            placeholder="Search bills…"
            className="min-w-64 flex-1 rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm placeholder:text-faint"
          />
          <select
            value={topic}
            onChange={(event) => set({ topic: event.target.value })}
            aria-label="Filter by subject"
            className="rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm"
          >
            <option value="">Every subject</option>
            {(topics.data?.data ?? [])
              .slice()
              .sort((a, b) => b.bills - a.bills)
              .map((t) => (
                <option key={t.policy_area} value={t.policy_area}>
                  {t.policy_area}
                </option>
              ))}
          </select>
          {filtered && (
            <button
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
              className="rounded-xl border border-line px-4 py-2.5 text-sm font-medium text-muted hover:text-ink"
            >
              Clear
            </button>
          )}
        </div>

        {bills.data && (
          <p className="mt-5 text-sm text-muted" role="status">
            <strong className="tnum font-semibold text-ink">
              {bills.data.total.toLocaleString()}
            </strong>{" "}
            {bills.data.total === 1 ? "bill" : "bills"}
            {filtered ? " match" : " still in play"}
            {bills.data.pages > 1 && (
              <>
                {" "}
                · page <span className="tnum">{bills.data.page}</span> of{" "}
                <span className="tnum">{bills.data.pages}</span>
              </>
            )}
          </p>
        )}

        {bills.error && (
          <div className="mt-6">
            <Problem error={bills.error} retry={bills.reload} />
          </div>
        )}
        {bills.loading && !bills.data && <Spinner label="Reading the record…" />}

        {bills.data && bills.data.bills.length === 0 && (
          <div className="mt-8 rounded-2xl border border-line bg-card p-10 text-center">
            <p className="font-display text-xl font-semibold">Nothing matched that.</p>
            <p className="mt-2 text-muted">Try a broader word, or clear the filters.</p>
          </div>
        )}

        {bills.data && bills.data.bills.length > 0 && (
          <ul
            className={`mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3 ${
              bills.loading ? "opacity-60" : ""
            }`}
          >
            {bills.data.bills.map((bill) => (
              <li key={bill.bill_id}>
                <BillCard bill={bill} />
              </li>
            ))}
          </ul>
        )}

        {bills.data && bills.data.pages > 1 && (
          <nav className="mt-10 flex items-center justify-center gap-3" aria-label="Pagination">
            <button
              disabled={page <= 1}
              onClick={() => set({ page: String(page - 1) })}
              className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
            >
              ← Previous
            </button>
            <span className="tnum text-sm text-muted">
              {bills.data.page} / {bills.data.pages}
            </span>
            <button
              disabled={page >= bills.data.pages}
              onClick={() => set({ page: String(page + 1) })}
              className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
            >
              Next →
            </button>
          </nav>
        )}

        <p className="mt-10 max-w-3xl text-sm text-faint">
          How far a bill has got is read from the actions Congress publishes and from the roll-call
          record, whichever is further along. Congress publishes no status field, so this is
          inference: a bill can be further along than we say — most passages are voice votes and
          leave no roll call — but not less far. Resolutions are left out, because they do not go to
          the President and cannot become law.
        </p>
      </div>
    </>
  );
}

/** The ladder, as filters. Each rung carries the count behind its own rate,
 *  because the rate is the reason to click it. */
function Ladder({
  rungs,
  active,
  onPick,
}: {
  rungs: Rung[];
  active: string;
  onPick: (key: string) => void;
}) {
  return (
    <section aria-labelledby="ladder">
      <h2 id="ladder" className="font-display text-lg font-semibold">
        How often does a bill this far along become law?
      </h2>
      <p className="mt-1 text-sm text-muted">
        Counted from the Congresses that have finished, so nothing still in progress is mistaken for
        a failure.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rungs
          .slice()
          .reverse()
          .map((rung) => {
            const tone = toneFor(rung.key);
            const on = active === rung.key;
            return (
              <button
                key={rung.key}
                onClick={() => onPick(on ? "" : rung.key)}
                aria-pressed={on}
                className={`rounded-2xl border p-4 text-left transition ${
                  on
                    ? "border-ink/30 bg-card shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)]"
                    : "border-line bg-card hover:border-ink/20"
                }`}
              >
                <p className="text-sm font-semibold">{rung.label}</p>
                <p className={`tnum mt-2 font-display text-3xl font-semibold ${tone.text}`}>
                  {rung.share === null ? "—" : oneIn(rung.share)}
                </p>
                <p className="tnum mt-1 text-xs text-faint">
                  {rung.of === 0
                    ? "no bills got this far"
                    : `${rung.became_law.toLocaleString()} of ${rung.of.toLocaleString()} became law`}
                </p>
              </button>
            );
          })}
      </div>
    </section>
  );
}

/**
 * One bill in the grid.
 *
 * Links inward, to this site's own page for the bill, rather than out to
 * congress.gov. The card is a summary of a page that now exists — the
 * ladder, the roll calls, the summary and the link out to the record are all
 * on it — and a card that jumped straight to the register skipped past all
 * of that.
 *
 * The last thing to happen to a bill is the most useful line on the card and
 * the easiest to misread, so it is labelled rather than left as a stray
 * sentence: "Motion to reconsider laid on the table" under a title means
 * nothing until you know it is the latest action.
 */
function BillCard({ bill }: { bill: MovingBill }) {
  const tone = toneFor(bill.stage.key);
  const odds = bill.odds;
  return (
    <Link
      to={`/bill/${bill.bill_id}`}
      className="flex h-full flex-col rounded-2xl border border-line bg-card p-5 transition hover:border-ink/25 hover:shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)]"
    >
      <span className="tnum text-sm font-semibold">{bill.display_name}</span>

      <span
        className={`mt-3 inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${tone.chip}`}
      >
        {reachedLabel(bill.stage.label, bill.chambers)}
      </span>

      <p className="mt-3 font-display leading-snug font-semibold text-balance">{bill.title}</p>

      {bill.latest_action && (
        <div className="mt-3">
          <p className="text-xs font-semibold tracking-wide text-muted uppercase">
            Latest Action
            {bill.latest_action_date && (
              <span className="tnum ml-1.5 font-normal text-faint normal-case">
                {when(bill.latest_action_date)}
              </span>
            )}
          </p>
          <p className="mt-0.5 line-clamp-2 text-xs text-faint">{bill.latest_action}</p>
        </div>
      )}

      <div className="mt-auto pt-4">
        <Progress rank={bill.stage.rank} bar={tone.bar} />
        {odds && odds.share !== null && (
          <p className="tnum mt-2 text-xs text-muted">
            <strong className="font-semibold text-ink">{oneIn(odds.share)}</strong> bills this far
            along became law ({odds.became_law.toLocaleString()} of {odds.of.toLocaleString()})
          </p>
        )}
      </div>
    </Link>
  );
}

/** Four rungs, filled to where the bill has got. */
function Progress({ rank, bar }: { rank: number; bar: string }) {
  return (
    <div className="flex gap-1" aria-hidden="true">
      {[0, 1, 2, 3].map((step) => (
        <span key={step} className={`h-1.5 flex-1 rounded-full ${step <= rank ? bar : "bg-sunk"}`} />
      ))}
    </div>
  );
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}
